import React, { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

// Type Definitions
export type WidgetSize = 'small' | 'medium' | 'large';

export interface RedirectConfig {
  path: string;
  queryParams?: Record<string, string | number | boolean>;
}

export interface WidgetCustomization {
  hidden?: boolean;
  order?: number;
  themeOverride?: Record<string, unknown>;
}

export interface WidgetMetadata {
  loading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
  isEmpty: boolean;
}

export interface WidgetHelpers<T> {
  refresh: () => void;
  formatData?: (data: T) => T;
  filterData?: (data: T) => T; // For array types, filters and returns filtered data
  navigate: () => void;
}

export interface WidgetContext {
  id: string;
  title: string;
  size: WidgetSize;
  redirect: RedirectConfig | undefined;
  hasRedirect: boolean;
}

export interface WidgetRenderProps<T> {
  data: T | undefined;
  metadata: WidgetMetadata;
  context: WidgetContext;
  helpers: WidgetHelpers<T>;
}

export interface ParentWidgetProps<T> {
  id: string;
  title: string;
  size?: WidgetSize;
  data?: T;
  children: ReactNode | ((props: WidgetRenderProps<T>) => ReactNode);
  redirect?: RedirectConfig;
  metadata?: Partial<WidgetMetadata>;
  onRefresh?: () => void;
  helpers?: Partial<WidgetHelpers<T>>;
  customization?: WidgetCustomization;
  showRedirectIndicator?: boolean;
  /** Permission key required to view this widget (e.g. widget:pending_approvals_bar). */
  requiredPermission: string;
  /** Optional custom predicate; when provided, overrides default empty detection. */
  isEmpty?: (data: T | undefined) => boolean;
  /** Optional controls rendered in the card header (clicks do not trigger redirect). */
  headerExtra?: ReactNode;
}

const ParentWidget = <T,>({
  id,
  title,
  size = 'medium',
  data,
  children,
  redirect,
  metadata,
  onRefresh,
  helpers: helpersProp,
  customization,
  showRedirectIndicator = true,
  requiredPermission,
  isEmpty: isEmptyProp,
  headerExtra,
}: ParentWidgetProps<T>): React.ReactElement | null => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Build metadata object (all hooks must run before any conditional return)
  const widgetMetadata: WidgetMetadata = useMemo(() => {
    let isEmpty: boolean;
    if (typeof isEmptyProp === 'function') {
      isEmpty = isEmptyProp(data);
    } else if (data === null || data === undefined) {
      isEmpty = true;
    } else if (Array.isArray(data)) {
      isEmpty = data.length === 0;
    } else if (typeof data === 'object') {
      isEmpty = Object.keys(data).length === 0;
    } else if (typeof data === 'string') {
      isEmpty = data.length === 0;
    } else if (typeof data === 'number') {
      isEmpty = false; // Numbers are never "empty"
    } else {
      isEmpty = false; // Other types (boolean, etc.) are not empty
    }

    return {
      loading: metadata?.loading ?? false,
      error: metadata?.error ?? null,
      lastUpdated: metadata?.lastUpdated ?? null,
      isEmpty,
    };
  }, [data, metadata, isEmptyProp]);

  // Build context object
  const widgetContext: WidgetContext = useMemo(() => ({
    id,
    title,
    size,
    redirect,
    hasRedirect: !!redirect,
  }), [id, title, size, redirect]);

  // Build helpers object
  const widgetHelpers: WidgetHelpers<T> = useMemo(() => {
    const handleNavigate = () => {
      if (redirect) {
        const queryString = redirect.queryParams
          ? new URLSearchParams(
              Object.entries(redirect.queryParams).reduce(
                (acc, [key, value]) => {
                  acc[key] = String(value);
                  return acc;
                },
                {} as Record<string, string>
              )
            ).toString()
          : '';
        
        const fullPath = queryString ? `${redirect.path}?${queryString}` : redirect.path;
        navigate(fullPath);
      }
    };

    return {
      refresh: onRefresh || (() => {}),
      formatData: helpersProp?.formatData,
      filterData: helpersProp?.filterData,
      navigate: handleNavigate,
    };
  }, [redirect, navigate, onRefresh, helpersProp]);

  // Build render props object
  const renderProps: WidgetRenderProps<T> = useMemo(() => ({
    data,
    metadata: widgetMetadata,
    context: widgetContext,
    helpers: widgetHelpers,
  }), [data, widgetMetadata, widgetContext, widgetHelpers]);

  // Handle redirect click
  const handleWidgetClick = () => {
    if (redirect) {
      widgetHelpers.navigate();
    }
  };

  // Size-based styling. `small` = KPI tile (eyebrow label + hero number, no grey header
  // bar); `medium`/`large` = chart widgets (keep the titled header bar).
  const isKpi = size === 'small';

  const sizeClasses = useMemo(
    () =>
      `tw-relative tw-flex tw-flex-col tw-min-w-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-shadow-card tw-mb-3${
        isKpi ? ' tw-h-full' : ''
      }`,
    [isKpi],
  );

  const headerClasses = useMemo(() => {
    // Same clean (no grey bar) header for every widget; charts get a hair more bottom
    // room before the plot area.
    return isKpi ? 'tw-px-4 tw-pt-3.5 tw-pb-0' : 'tw-px-4 tw-pt-3.5 tw-pb-1';
  }, [isKpi]);

  const headerTextClasses = useMemo(() => {
    // One title language across the whole dashboard: a left-aligned eyebrow in Title Case (V2-062).
    // Fixed min-height + line-clamp so cards line up regardless of title length —
    // KPI reserves 3 lines (value must start at the same Y); charts reserve 2 so the
    // two bar-graph cards match even when one title wraps and the other doesn't.
    // W-M16a: the KPI title box is a fixed height (not a minimum) so the number below it starts
    // at the same Y in every tile and every section, whether the title takes one line or three.
    if (isKpi) {
      return 'tw-mb-0 tw-h-[2.6rem] tw-overflow-hidden tw-text-[0.7rem] tw-font-semibold tw-leading-[1.2] tw-tracking-[0.06em] tw-text-content-muted tw-line-clamp-3';
    }
    return 'tw-mb-0 tw-min-h-[2rem] tw-text-[0.72rem] tw-font-semibold tw-leading-[1.2] tw-tracking-[0.06em] tw-text-content-muted tw-line-clamp-2';
  }, [isKpi]);

  const bodyClasses = useMemo(() => {
    const sizeSpecificClasses = {
      small: 'tw-px-4 tw-pb-4 tw-pt-1',
      medium: 'tw-p-4',
      large: 'tw-p-4',
    };
    return `tw-flex-auto ${sizeSpecificClasses[size]}`.trim();
  }, [size]);

  const isClickable = !!redirect && showRedirectIndicator;

  const widgetClassName = useMemo(
    () => `${sizeClasses}${isClickable ? ' widget-clickable' : ''}`,
    [sizeClasses, isClickable],
  );

  // Early returns only after all hooks have been called
  if (!hasPermission(requiredPermission)) {
    return null;
  }
  if (customization?.hidden) {
    return null;
  }

  // Render children
  const renderChildren = (): ReactNode => {
    if (typeof children === 'function') {
      return children(renderProps);
    }
    return children;
  };

  return (
    <div
      className={widgetClassName}
      onClick={redirect ? handleWidgetClick : undefined}
    >
      <div className={headerClasses}>
        {headerExtra ? (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full tw-min-w-0">
            <h6 className={`${headerTextClasses} tw-text-left tw-mb-0 tw-grow tw-min-w-0`} title={title}>{title}</h6>
            <div
              className="tw-shrink-0"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {headerExtra}
            </div>
          </div>
        ) : (
          <h6 className={`${headerTextClasses} tw-text-left`} title={title}>{title}</h6>
        )}
      </div>
      <div className={bodyClasses}>
        {renderChildren()}
      </div>
    </div>
  );
};

export default ParentWidget;
